<template>
  <div
    ref="card"
    class="em-home-card is-sliding-in"
    :class="{
      'has-pointer': interactivity === 'flip',
      'is-x-large': cardType === 'xLarge',
      'is-large': cardType === 'large',
      'is-medium-large': cardType === 'medium-large',
      'is-medium': cardType === 'medium',
      'is-small': cardType === 'small',
      'is-x-small': cardType === 'xSmall',
    }"
    @click="handleFlip">
    <component
      :is="interactivity === 'link' ? SharedBaseLink : 'div'"
      class="cards-container"
      :link="link"
      @mousemove="tiltCard"
      @mouseenter="isHovered = true"
      @mouseleave="onMouseLeave">
      <div
        ref="cardFront"
        class="card card-front"
        :class="[cardOptions.paddingClass, isDark ? 'is-dark' : 'is-light']"
        :style="{
          alignItems: horizontalTextAlign === 'left' ? 'flex-start' : 'center',
          justifyContent:
            verticalTextAlign === 'top' ? 'flex-start' : verticalTextAlign === 'center' ? 'center' : 'flex-end',
        }">
        <!-- Front card  -->
        <div
          class="card-front-inner"
          :class="{
            'has-max-width-x-large': cardType === 'xLarge',
            'has-max-width-large': cardType === 'large',
            'has-max-width-medium': cardType === 'medium' || cardType === 'medium-large',
            'has-max-width-small': cardType === 'small' || cardType === 'xSmall',
          }"
          :style="{ textAlign: horizontalTextAlign }">
          <!-- Front text  -->
          <p
            v-if="textLayout === 'overline_headline' && frontSideOverline"
            ref="text"
            class="front-overline"
            :class="[cardOptions.smallTextClass, cardOptions.smallTextMargin]">
            {{ frontSideOverline }}
          </p>
          <div ref="headline">
            <SharedBaseRichtext :class="cardOptions.largeTextClass" :text="frontSideHeadline" :isProseEnabled="false" />
          </div>
          <p
            v-if="textLayout === 'headline_subline' && frontSideSubline"
            ref="text"
            class="front-subline"
            :class="[cardOptions.smallTextClass, cardOptions.smallTextMargin]">
            {{ frontSideSubline }}
          </p>
        </div>

        <!-- Buttons  -->
        <div
          v-if="interactivity === 'link'"
          class="icon-button is-primary"
          :tabindex="-1"
          :class="[verticalTextAlign === 'top' ? 'is-bottom' : 'is-top', { 'is-hovered': isHovered }]">
          <BaseIcon name="fi_arrow-right" :size="16" color="inherit" />
        </div>
        <button
          v-if="interactivity === 'flip'"
          :tabindex="isFlipped ? -1 : undefined"
          class="icon-button"
          :class="[isDark ? 'is-dark' : 'is-light', verticalTextAlign === 'top' ? 'is-bottom' : 'is-top']"
          :aria-label="t('global.flipCard')"
          @click="handleFlip">
          <BaseIcon name="fi_repeat" :size="16" color="inherit" />
        </button>

        <!-- Background Media  -->
        <div v-if="!!frontSideVideo || !!frontSideImage" class="card-media">
          <SharedBaseVideo
            v-if="!!frontSideVideo && isVideo(frontSideVideo.filename)"
            :src="frontSideVideo.filename"
            :autoplay="true"
            :loop="true"
            :loadWhenInView="cardType !== 'xLarge'"
            :poster="frontSideImage?.filename"
            :posterBreakPointsWidthMap="cardOptions.breakpointsWidthMap"
            :posterHeightRatio="cardAspectRatio"
            :preloadPoster="cardType === 'xLarge'"
            class="media is-video" />
          <SharedBaseImage
            v-else
            :image="frontSideImage"
            :breakpointsWidthMap="cardOptions.breakpointsWidthMap"
            :heightRatio="cardAspectRatio"
            class="media"
            :preload="cardType === 'xLarge'"
            objectCover />
        </div>
      </div>

      <!-- Back card  -->
      <div v-if="interactivity === 'flip'" ref="cardBack" class="card card-back" :class="cardOptions.paddingClass">
        <p v-if="backSideText" class="card-back-text">
          {{ backSideText }}
        </p>
        <UtilButton
          v-if="backSideCta"
          :blok="backSideCta"
          class="card-back-button"
          :tabindex="isFlipped ? undefined : -1" />
        <button
          class="icon-button is-dark is-top"
          :tabindex="isFlipped ? undefined : -1"
          :aria-label="t('global.flipCard')"
          @click="handleFlip">
          <BaseIcon name="fi_repeat" :size="16" color="inherit" />
        </button>
      </div>
    </component>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import UtilButton from '@/components/storyblok/utils/UtilButton/UtilButton.vue';
import { useElementSize, watchDebounced } from '@vueuse/core';
import {
  SharedBaseImage,
  SharedBaseLink,
  SharedBaseRichtext,
  SharedBaseVideo,
} from '@seb-platform/shared/components/base';
import { create3dCard } from '@seb-platform/shared/utils/create3dCard';
import { isVideo } from '@seb-platform/shared/utils/media';
import { BaseIcon } from '~/components/base';
import type { EMHomeCard } from './EMHome.types';

const props = defineProps<EMHomeCard>();

const emit = defineEmits<{ (e: 'flip', id: string): void }>();

const $card = useTemplateRef('card');
const $cardFront = useTemplateRef('cardFront');
const $cardBack = useTemplateRef('cardBack');
const $headline = useTemplateRef('headline');
const $text = useTemplateRef('text');
const tl = ref<GSAPTimeline>();
const isFlipped = ref(props.isFlipped);
const isHovered = ref(false);
const cardAspectRatio = ref<[number, number]>([1, 1]);

const { width, height } = useElementSize($card);
const { t } = useI18n();

function handleFlip(e: MouseEvent) {
  if (props.interactivity !== 'flip') {
    return;
  }
  e.stopPropagation();
  emit('flip', props.uid);
}

function onMouseLeave() {
  resetTilt();
  isHovered.value = false;
}

interface CardOptions {
  largeTextClass: string;
  smallTextClass?: string;
  paddingClass: string;
  perspective: number;
  breakpointsWidthMap: Record<number, number>;
  smallTextMargin?: string;
}

const cardOptionsMap: Record<EMHomeCard['cardType'], CardOptions> = {
  xLarge: {
    largeTextClass: 'card-xlarge-text-large',
    smallTextClass: 'card-xlarge-text-small',
    smallTextMargin: 'has-margin-large',
    paddingClass: 'padding-large',
    perspective: 5800,
    breakpointsWidthMap: {
      0: 345,
      375: 706,
      768: 700,
      1024: 878,
      1280: 988,
      1440: 1318,
      1920: 1434,
    },
  },
  large: {
    largeTextClass: 'card-large-text-large',
    smallTextClass: 'card-large-text-small',
    smallTextMargin: 'has-margin-medium',
    paddingClass: 'padding-large',
    perspective: 4800,
    breakpointsWidthMap: {
      0: 345,
      375: 706,
      768: 459,
      1024: 577,
      1280: 651,
      1440: 871,
      1920: 948,
    },
  },
  'medium-large': {
    // The medium card in the 1 medium 1 small variant
    largeTextClass: 'card-medium-large-text-large',
    smallTextClass: 'card-medium-large-text-small',
    smallTextMargin: 'has-margin-small',
    paddingClass: 'padding-medium-large',
    perspective: 3400,
    breakpointsWidthMap: {
      0: 345,
      375: 706,
      768: 218,
      1024: 277,
      1280: 314,
      1440: 424,
      1920: 462,
    },
  },
  medium: {
    // The medium card in the 2 medium variant
    largeTextClass: 'card-medium-text-large',
    smallTextClass: 'card-medium-text-small',
    smallTextMargin: 'has-margin-small',
    paddingClass: 'padding-medium',
    perspective: 3000,
    breakpointsWidthMap: {
      0: 345,
      375: 706,
      768: 218,
      1024: 277,
      1280: 314,
      1440: 424,
      1920: 462,
    },
  },
  small: {
    largeTextClass: 'card-small-text-large',
    paddingClass: 'padding-small',
    perspective: 1500,
    breakpointsWidthMap: {
      0: 345,
      375: 348,
      768: 218,
      1024: 277,
      1280: 314,
      1440: 424,
      1920: 462,
    },
  },
  xSmall: {
    // The small cards in 1 large 2 small variant
    largeTextClass: 'card-xsmall-text-large',
    paddingClass: 'padding-small',
    perspective: 1500,
    breakpointsWidthMap: {
      0: 345,
      375: 348,
      768: 218,
      1024: 277,
      1280: 314,
      1440: 424,
      1920: 462,
    },
  },
};
const cardOptions = cardOptionsMap[props.cardType];

const { flipCard, resetTilt, tiltCard, setCard } = create3dCard({
  $card,
  $cardFront,
  $cardBack,
  $headline,
  $text,
  isFlipped,
  isFlippable: props.interactivity === 'flip',
  tl,
  options: { transformPerspective: cardOptions.perspective },
});

watch(() => props.isFlipped, flipCard);

/**
 * We need to manually set the card aspect ratio. We keep a fixed with to reduce the image variants
 * and the aspect ratio is rounded.
 */
watchDebounced(
  () => [width.value, height.value],
  () => {
    if (width.value && height.value) {
      cardAspectRatio.value = [Math.round(width.value / 10), Math.round(height.value / 10)];
    }
  },
  { debounce: 200, immediate: true }
);

onMounted(() => {
  setCard();

  // Just in case that the EMHome component doesn't remove the is-sliding-in class
  setTimeout(() => {
    $card.value?.classList.remove('is-sliding-in');
  }, 3000);
});
</script>

<style lang="scss" scoped src="./EMHomeCard.scss" />
